import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type { User } from 'oidc-client-ts';
import { userManager } from 'services/oidcConfig';

// Define the base URL for your API
const baseUrl = process.env.REACT_APP_API_URL || '/ai-signal';
const env = process.env.REACT_APP_ENV;
const isLocal = env === 'local';
const isStandalone = process.env.REACT_APP_TYPE === 'standalone';
// Refresh tokens are single-use (rotation), so concurrent requests must never
// each start their own renew — the first one burns the token and the rest fail
// with "refresh token was already used". All callers share one in-flight renew.
let inFlightRenew: Promise<User | null> | null = null;

// OAuth 2.0 refresh token grant (RFC 6749 §1.5 / §6). oidc-client-ts performs it
// under the hood in signinSilent() whenever the stored user has a refresh_token
// (granted by the `offline_access` scope), so no iframe / silent-redirect page is
// involved. The renewed user is written back to the userStore, which is why the
// fresh access token is afterwards readable via userManager.getUser().
const renewViaRefreshToken = async (): Promise<User | null> => {
  if (!inFlightRenew) {
    inFlightRenew = userManager.signinSilent().finally(() => {
      inFlightRenew = null;
    });
  }

  try {
    return await inFlightRenew;
  } catch (err) {
    // Renew is unrecoverable (refresh token expired/revoked): fall through
    // unauthenticated and let the 401 handling below redirect to login.
    return null;
  }
};

const getValidUser = async (): Promise<User | null> => {
  const user = await userManager.getUser();
  if (isLocal || !user?.expired) {
    return user;
  }

  // Normal renewal is handled proactively by automaticSilentRenew; this only
  // covers a token that already expired while the app was closed or asleep.
  return renewViaRefreshToken();
};

const accessTokenOf = (user: User | null) =>
  user && !user.expired ? user.access_token : null;

// Custom base query with redirect handling
export const baseQueryWithAuth = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  let oidcUser = await getValidUser();
  const makeBaseQuery = (token: string | null) =>
    fetchBaseQuery({
      baseUrl,
      credentials: isLocal ? undefined : 'include',
      prepareHeaders: headers => {
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
        headers.set('content-type', 'application/json');
        headers.set('X-Request-ID', crypto.randomUUID());
        if (!isStandalone) {
          try {
            const csrf = sessionStorage.getItem('csrfToken');
            if (csrf) {
              headers.set('csrf', csrf);
            }
          } catch (_) {}
        }
        return headers;
      },
    });

  let baseQuery = makeBaseQuery(accessTokenOf(oidcUser));

  // Execute request with rate-limit (429) retry loop
  let result = await baseQuery(args, api, extraOptions);

  const isRateLimited = (r: any) => {
    if (!r || !r.error) return false;
    const s =
      typeof r.error.status === 'number'
        ? r.error.status
        : (r.error as any).originalStatus;
    return s === 429;
  };

  let rateLimitRetryCount = 0;
  while (isRateLimited(result)) {
    rateLimitRetryCount += 1;
    // Annotate attempt count so UI could optionally surface it
    try {
      (result.error as any).rateLimitRetryCount = rateLimitRetryCount;
    } catch (_) {}
    // Fixed backoff of 5 seconds per requirement (could be made dynamic later)
    await new Promise(res => setTimeout(res, 5000));
    result = await baseQuery(args, api, extraOptions);
    if (!isRateLimited(result)) {
      // If still error but not 429, mark exhaustion of 429 loop
      if (result.error) {
        try {
          (result.error as any).rateLimitFinalAttempt = rateLimitRetryCount;
        } catch (_) {}
      }
      break;
    }
  }

  // The token can also be rejected by the API while the client still considers it
  // valid (clock skew, server-side revocation, a session invalidated elsewhere).
  // Spend one refresh token grant on it and replay the request before giving up.
  const isUnauthorized = (r: any) => !isLocal && r?.error?.status === 401;

  if (isUnauthorized(result)) {
    oidcUser = await renewViaRefreshToken();
    const renewedToken = accessTokenOf(oidcUser);

    if (renewedToken) {
      baseQuery = makeBaseQuery(renewedToken);
      result = await baseQuery(args, api, extraOptions);
    }
  }

  if (result.meta?.response?.redirected) {
    window.location.href = isStandalone
      ? '/auth'
      : 'https://suite.xovi.net/user/login';

    return {
      error: {
        ...result.error,
        status: 'AUTHENTICATION_ERROR',
        data: 'Authentication required. Redirecting to login...',
      },
    };
  }
  // Handle errors
  if (result.error) {
    if (typeof result.error.status === 'number') {
      const status = result.error.status;

      if (status === 401 || status === 403) {
        await userManager.signinRedirect();
        return {
          error: {
            ...result.error,
            status: 'AUTHENTICATION_ERROR',
            data: 'Authentication required. Redirecting to login...',
          },
        };
      }

      if (status === 302) {
        window.location.href = isStandalone
          ? '/auth'
          : 'https://suite.xovi.net/user/login';

        return {
          error: {
            ...result.error,
            status: 'AUTHENTICATION_ERROR',
            data: 'Authentication required. Redirecting to login...',
          },
        };
      }
    }
  }

  return result;
};
